import type { Command } from 'commander';
import { unwrapDataResponse } from '@wport/core';
import { enterpriseGet } from '../../lib/enterprise-client';
import { resolveApiKey } from '../../lib/credentials-store';
import { resolveContext } from '../../lib/global-opts';
import { dim, printJson, type OutputFormat } from '../../lib/output';

/** 欄位對齊 server EnterpriseUsageVm（enterprise-usage.vm.ts，pm_41 批次 2）。 */
export interface EnterpriseUsage {
	period?: string;
	quota?: { limit?: number; used?: number; remaining?: number };
	rate_limit?: { limit?: number; window_seconds?: number };
	[k: string]: unknown;
}

function num(value: number | undefined): string {
	return typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';
}

/**
 * `usage` 核心：驗 key → GET /usage → 輸出當期用量摘要。與 commander 註冊分離便於測試。
 * GET /usage 回 DataResponse（單一物件，非陣列）。
 *
 * quota 語意（PRD DR-8，talent#233 起 prod 生效）：月配額計「所有認證通過的 API 呼叫」
 * （1 call = 1 count；whoami/usage 與 key rotate 豁免）——顯示需點明計量範圍，
 * 使用者才不會把豁免端點的「不動」誤讀成計數壞掉（issue #41/#43 的教訓）。
 *
 * rate_limit 為「每把 key 的設定上限」（非即時剩餘）；即時 per-window 剩餘由被限流端點
 * （jobs 寫入）的 x-ratelimit-remaining response header 呈現，此讀端點不掛 throttle。
 */
export async function runUsage(
	ctx: { baseUrl: string; locale: string; timeoutMs: number; format: OutputFormat; color: boolean },
	apiKey: string
): Promise<void> {
	const { body } = await enterpriseGet(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		'/usage'
	);
	const usage = unwrapDataResponse<EnterpriseUsage>(body);

	if (ctx.format === 'json') {
		printJson(usage);
		return;
	}

	const quota = usage.quota ?? {};
	const rate = usage.rate_limit ?? {};
	const lines = [
		`period:         ${usage.period ?? '—'}`,
		`monthly quota:  ${num(quota.used)} / ${num(quota.limit)} calls used  (${num(quota.remaining)} remaining)`,
		`rate limit:     ${num(rate.limit)} requests / ${num(rate.window_seconds)}s per key`,
	];
	process.stdout.write(lines.join('\n') + '\n');
	process.stdout.write(
		dim(
			'Monthly quota meters every authenticated API call; whoami/usage and key rotate are exempt.',
			ctx.color
		) + '\n'
	);
	process.stdout.write(
		dim('Live per-window rate-limit headroom is reported via response headers on write requests.', ctx.color) + '\n'
	);
}

export function registerEnterpriseUsage(parent: Command): void {
	parent
		.command('usage')
		.description('Show this month API quota usage and rate-limit ceiling')
		.action(async (_flags: unknown, command: Command) => {
			const ctx = resolveContext(command);
			const globals = command.optsWithGlobals() as { apiKey?: string };
			const { key } = resolveApiKey(globals.apiKey);
			await runUsage(ctx, key);
		});
}

// Internal helpers exposed for tests. Not part of the public CLI API.
export const __test__ = { num };
